// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

// Routes
export const ROUTES = {
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  AGENTS: '/agents',
  AGENTS_CREATE: '/agents/create',
  AGENTS_EDIT: '/agents/:agentId/edit',
  INTEGRATIONS: '/integrations',
  KNOWLEDGE_BASE: '/knowledge-base',
  KNOWLEDGE_BASE_CREATE: '/knowledge-base/create',
  KNOWLEDGE_BASE_DETAIL: '/knowledge-base/:kbId',
  KNOWLEDGE_BASE_EDIT: '/knowledge-base/:kbId/edit',
  KNOWLEDGE_BASE_UPLOAD: '/knowledge-base/:kbId/upload',
  EXTERNAL_API: '/external-api',
  EXTERNAL_API_CREATE: '/external-api/create',
  EXTERNAL_API_EDIT: '/external-api/:keyId/edit',
  TOOLS: '/tools',
  TOOLS_DATA_COLLECTION: '/tools/data-collection',
  TOOLS_DATA_COLLECTION_DATA: '/tools/data-collection/data',
  TOOLS_DATA_COLLECTION_SESSIONS: '/tools/data-collection/sessions',
};

// User Roles and Permissions
export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  EDITOR: 'editor',
  VIEWER: 'viewer',
};

export const PERMISSIONS = {
  [ROLES.OWNER]: ['*'],
  [ROLES.ADMIN]: [
    'agents:create',
    'agents:edit',
    'agents:delete',
    'agents:view',
    'integrations:create',
    'integrations:edit',
    'integrations:delete',
    'integrations:view',
    'knowledge:create',
    'knowledge:edit',
    'knowledge:delete',
    'knowledge:view',
    'users:view',
    'external_api:create',
    'external_api:edit',
    'external_api:delete',
    'external_api:view',
    'tools:view',
    'tools:data_collection:view',
  ],
  [ROLES.EDITOR]: [
    'agents:create',
    'agents:edit',
    'agents:view',
    'integrations:edit',
    'integrations:view',
    'knowledge:create',
    'knowledge:edit',
    'knowledge:view',
    'external_api:create',
    'external_api:edit',
    'external_api:view',
  ],
  [ROLES.VIEWER]: [
    'agents:view',
    'integrations:view',
    'knowledge:view',
    'external_api:view',
  ],
};

// Agent Configuration
export const AGENT_CONFIG = {
  MODELS: [
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
  ],
  TEMPERATURE: {
    MIN: 0,
    MAX: 2,
    DEFAULT: 0.7,
    STEP: 0.1,
  },
  MAX_TOKENS: {
    MIN: 1,
    MAX: 4096,
    DEFAULT: 1500,
  },
  TOP_P: {
    MIN: 0,
    MAX: 1,
    DEFAULT: 0.9,
    STEP: 0.1,
  },
  PRESENCE_PENALTY: {
    MIN: -2,
    MAX: 2,
    DEFAULT: 0.1,
    STEP: 0.1,
  },
  FREQUENCY_PENALTY: {
    MIN: -2,
    MAX: 2,
    DEFAULT: 0.1,
    STEP: 0.1,
  },
};

// Available Tools
export const TOOLS = {
  GOOGLE_CALENDAR: {
    name: 'calendar',
    displayName: 'Google Calendar',
    description: 'Agendar reuniões e eventos no Google Calendar',
    icon: '📅',
    category: 'calendar',
  },
  PIPEDRIVE: {
    name: 'pipedrive',
    displayName: 'Pipedrive CRM',
    description: 'Gerenciar leads e oportunidades no Pipedrive',
    icon: '📊',
    category: 'crm',
  },
};

// Integration Types
export const INTEGRATION_TYPES = {
  GOOGLE_CALENDAR: 'google_calendar',
  PIPEDRIVE: 'pipedrive',
};

// Status Options
export const STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DRAFT: 'draft',
};

// Channels
export const CHANNELS = {
  API: 'api',
  WEB: 'web',
  WHATSAPP: 'whatsapp',
  TELEGRAM: 'telegram',
};

// External API Constants
export const EXTERNAL_API = {
  TIERS: {
    DEV: 'dev',
    BASIC: 'basic',
    PRO: 'pro',
    ENTERPRISE: 'enterprise',
  },
  KEY_PREFIXES: {
    LIVE: 'pk_live_',
    TEST: 'pk_test_',
  },
  CHANNELS: {
    API: 'api',
    WHATSAPP: 'whatsapp',
    TELEGRAM: 'telegram',
    WEB: 'web',
  },
  CODE_LANGUAGES: {
    CURL: 'curl',
    PYTHON: 'python',
    JAVASCRIPT: 'javascript',
    NODEJS: 'nodejs',
  },
};

// Guardrails Types
export const GUARDRAILS_TYPES = {
  INPUT: {
    CONTENT_FILTER: 'content_filter',
    RATE_LIMIT: 'rate_limit',
    LANGUAGE_DETECTION: 'language_detection',
  },
  OUTPUT: {
    COMPLIANCE_CHECK: 'compliance_check',
    BRAND_SAFETY: 'brand_safety',
    TONE_CONTROL: 'tone_control',
  },
};

// UI Constants
export const UI = {
  SIDEBAR_WIDTH: 280,
  HEADER_HEIGHT: 64,
  DRAWER_WIDTH: 240,
  ANIMATION_DURATION: 300,
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Erro de conexão. Verifique sua internet.',
  UNAUTHORIZED: 'Você não tem permissão para esta ação.',
  VALIDATION_ERROR: 'Dados inválidos. Verifique os campos.',
  SERVER_ERROR: 'Erro interno do servidor. Tente novamente.',
  NOT_FOUND: 'Recurso não encontrado.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  AGENT_CREATED: 'Agente criado com sucesso!',
  AGENT_UPDATED: 'Agente atualizado com sucesso!',
  AGENT_DELETED: 'Agente excluído com sucesso!',
  INTEGRATION_SAVED: 'Integração configurada com sucesso!',
  LOGIN_SUCCESS: 'Login realizado com sucesso!',
};

// Default Values
export const DEFAULTS = {
  AGENT: {
    model: '', // Será definido dinamicamente pela API
    temperature: 0.7,
    max_tokens: 1500,
    top_p: 0.9,
    presence_penalty: 0.1,
    frequency_penalty: 0.1,
    is_active: true,
    channels: {
      api: true,
      web: true,
      whatsapp: false,
      telegram: false,
    },
  },
  PAGINATION: {
    PAGE_SIZE: 12,
    PAGE_SIZE_OPTIONS: [12, 24, 48],
  },
}; 